/**
 * React host for the shared (Lit) embedding/search badge (sl: badge-uniformity).
 *
 * SOLID: D — the badge's look + interaction live in ONE place
 * (`@blocksuite/affine/shared/services` → `renderEmbeddingBadge`). React-side
 * surfaces (the PDF viewer) render that exact Lit template through this thin
 * host instead of re-implementing the markup/CSS, so the badge can no longer
 * drift between block types and the PDF viewer.
 *
 * The shell stylesheet (`embeddingBadgeStyles`) is injected once globally; its
 * class names are not scoped, so the light-DOM badge rendered here picks up the
 * identical styling that the Lit image/attachment badges get in their shadow
 * roots.
 */
import {
  embeddingBadgeStyles,
  type EmbeddingBadgeViewModel,
  renderEmbeddingBadge,
} from '@blocksuite/affine/shared/services';
import { render } from 'lit';
import { useEffect, useRef } from 'react';

let stylesInjected = false;

function ensureBadgeStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-embedding-badge-shell', '');
  style.textContent = embeddingBadgeStyles.cssText;
  document.head.append(style);
}

export function EmbeddingBadge({
  vm,
  className,
  onOutsidePointerDown,
}: {
  vm: EmbeddingBadgeViewModel | null;
  className?: string;
  onOutsidePointerDown?: () => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ensureBadgeStyles();
    const host = hostRef.current;
    if (!host) return;
    render(vm ? renderEmbeddingBadge(vm) : null, host);
    return () => {
      render(null, host);
    };
  }, [vm]);

  useEffect(() => {
    if (!onOutsidePointerDown || typeof document === 'undefined') return;
    const handlePointerDown = (event: PointerEvent) => {
      const host = hostRef.current;
      if (!host || event.composedPath().includes(host)) return;
      onOutsidePointerDown();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [onOutsidePointerDown]);

  if (!vm) return null;
  return <div ref={hostRef} className={className} />;
}
